import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import {
    FormInputComponent,
    InputComponentConfig,
    SharedModule,
} from '@vendure/admin-ui/core';

interface PortfolioCustomer {
    id?: string | number;
    firstName?: string;
    lastName?: string;
    emailAddress?: string;
    phoneNumber?: string;
    customFields?: {
        document?: string;
    };
}

interface PortfolioChannel {
    id?: string | number;
    code?: string;
    customFields?: {
        channelName?: string;
    };
}

interface PortfolioItem {
    id?: string | number;
    customer?: PortfolioCustomer;
    channel?: PortfolioChannel;
}

@Component({
    standalone: true,
    selector: 'seller-portfolio-form-input',
    imports: [CommonModule, SharedModule],
    templateUrl: './input-form.component.html',
    styleUrls: ['./input-form.component.scss'],
})
export class SellerPortfolioFormInputComponent
    implements FormInputComponent<InputComponentConfig>, OnChanges
{
    @Input() readonly = false;
    @Input() formControl!: FormControl;
    @Input() config!: InputComponentConfig;

    readonly pageSize = 50;
    currentPage = 1;

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.formControl) {
            this.currentPage = 1;
        }
    }

    get portfolio(): PortfolioItem[] {
        const value = this.formControl?.value;
        return Array.isArray(value) ? value : [];
    }

    get totalItems(): number {
        return this.portfolio.length;
    }

    get totalPages(): number {
        const pages = Math.ceil(this.totalItems / this.pageSize);
        return pages > 0 ? pages : 1;
    }

    get visibleItems(): PortfolioItem[] {
        const start = (this.currentPage - 1) * this.pageSize;
        return this.portfolio.slice(start, start + this.pageSize);
    }

    get hasData(): boolean {
        return this.totalItems > 0;
    }

    previousPage(): void {
        if (this.currentPage > 1) {
            this.currentPage -= 1;
        }
    }

    nextPage(): void {
        if (this.currentPage < this.totalPages) {
            this.currentPage += 1;
        }
    }

    openCustomerDetails(customerId?: string | number): void {
        if (!customerId) {
            return;
        }
        const url = `/customers/${customerId}`;
        window.open(url, '_blank');
    }

    customerName(item: PortfolioItem): string {
        const firstName = item.customer?.firstName ?? '';
        const lastName = item.customer?.lastName ?? '';
        const fullName = `${firstName} ${lastName}`.trim();
        return fullName || '—';
    }

    customerDocument(item: PortfolioItem): string {
        return item.customer?.customFields?.document ?? '—';
    }

    customerEmail(item: PortfolioItem): string {
        return item.customer?.emailAddress ?? '—';
    }

    customerPhone(item: PortfolioItem): string {
        return item.customer?.phoneNumber ?? '—';
    }

    channelName(item: PortfolioItem): string {
        return (
            item.channel?.customFields?.channelName ?? item.channel?.code ?? '—'
        );
    }
}
